import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Salad, Target, Crown } from "lucide-react";

const TIERS = [
  {
    id: "basic",
    name: "Foundational",
    tag: "Starter",
    price: "$50",
    per: "/ month",
    Icon: Salad,
    features: [
      "Pick your focus: Meal Plans OR Workout Plans",
      "12 AI-generated items per plan",
      "Save favorites + full library access",
      "Plain-English coaching cues",
    ],
    excluded: ["Progress Tracker", "Both Tracks at Once"],
    highlight: false,
  },
  {
    id: "accountability",
    name: "Practitioner",
    tag: "Most Picked",
    price: "$100",
    per: "/ month",
    Icon: Target,
    features: [
      "Everything in Foundational",
      "Pick Meal OR Workout track",
      "Progress Tracker + 30-day check-ins",
      "Body composition tracking",
    ],
    excluded: ["Both Tracks at Once"],
    highlight: true,
  },
  {
    id: "advance",
    name: "Master",
    tag: "Full Stack",
    price: "$150",
    per: "/ month",
    Icon: Crown,
    features: [
      "Everything in Practitioner",
      "FULL access: Meal + Workout Plans",
      "Strength · Cardio · Recovery libraries",
      "Service Billing dashboard",
    ],
    excluded: [],
    highlight: false,
  },
];

export default function Programs() {
  return (
    <section
      id="programs"
      className="relative border-t border-zinc-900 py-24 md:py-32 px-6 md:px-12"
      data-testid="programs-section"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-14 md:mb-20 max-w-3xl">
          <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#E5B43A] mb-4">
            // Membership
          </div>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[0.95]">
            Lock in your <span className="text-[#E5B43A]">commitment.</span>
          </h2>
          <p className="mt-6 text-zinc-400 text-base leading-relaxed">
            Three levels. Cancel anytime. Every tier unlocks more of the
            Gain Time training stack — built for every effort level from
            first-timer to seasoned athlete.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
          {TIERS.map((t, i) => {
            const Icon = t.Icon;
            return (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`relative border p-7 md:p-8 flex flex-col min-h-[560px] transition-colors ${
                  t.highlight
                    ? "border-[#E5B43A] bg-[#141414]"
                    : "border-zinc-800 bg-[#141414] hover:border-zinc-600"
                }`}
                data-testid={`tier-card-${t.id}`}
              >
                {t.highlight ? (
                  <span className="self-start bg-[#E5B43A] text-black text-[10px] font-bold tracking-[0.3em] uppercase px-2 py-1 mb-4">
                    {t.tag}
                  </span>
                ) : (
                  <span className="self-start text-[10px] font-bold tracking-[0.3em] uppercase text-zinc-500 mb-4">
                    {t.tag}
                  </span>
                )}

                <Icon className="text-[#E5B43A] mb-4" size={26} />

                <h3 className="font-display text-2xl md:text-3xl mb-2">{t.name}</h3>
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="font-display text-5xl text-white">{t.price}</span>
                  <span className="text-xs text-zinc-500">{t.per}</span>
                </div>

                <ul className="space-y-2.5 mb-6 flex-1 text-sm">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-zinc-300">
                      <Check size={14} className="text-[#E5B43A] mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                  {t.excluded.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-zinc-600 line-through">
                      <span className="shrink-0 w-3.5 inline-block">—</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/membership"
                  data-testid={`tier-cta-${t.id}`}
                  className={`mt-auto inline-flex items-center justify-center gap-2 font-bold uppercase tracking-widest px-5 py-3.5 text-xs transition-colors duration-300 ${
                    t.highlight || t.id === "advance"
                      ? "bg-[#E5B43A] text-black hover:bg-white hover:text-[#0A0A0A]"
                      : "border border-zinc-700 text-white hover:border-[#E5B43A] hover:text-[#E5B43A]"
                  }`}
                >
                  Register <ArrowRight size={14} />
                </Link>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-10 text-xs tracking-[0.25em] uppercase text-zinc-500 text-center">
          Paid via Square · Cancel anytime · 30-day access per cycle
        </div>

        <div className="mt-12 border border-zinc-800 bg-[#0F0F0F] p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5" data-testid="in-person-cta">
          <div className="min-w-0">
            <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#E5B43A] mb-2">
              In-Person Option
            </div>
            <h3 className="font-display text-2xl md:text-3xl mb-2 leading-tight">
              Prefer in-person training?
            </h3>
            <p className="text-zinc-400 text-sm leading-relaxed max-w-xl">
              Schedule a <span className="text-white font-semibold">$25 assessment</span> in
              Houston. Cost is credited toward your training package once you sign on.
            </p>
          </div>
          <a
            href="#assessment"
            data-testid="in-person-cta-link"
            className="shrink-0 inline-flex items-center justify-center gap-2 bg-[#E5B43A] text-black font-bold uppercase tracking-widest px-6 py-4 text-xs hover:bg-white hover:text-[#0A0A0A] transition-colors"
          >
            Book Assessment <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </section>
  );
}
